import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Key, Mail, Lock, ArrowLeft } from 'lucide-react';
import { authAPI } from '../../services/api';
import OtpModal from './OtpModal';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpError, setOtpError] = useState('');
  
  const navigate = useNavigate();

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setShowOtpModal(true);
      setSuccess('OTP sent to your email');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otpCode: string) => {
    setOtp(otpCode);
    setShowOtpModal(false);
    setShowResetForm(true);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await authAPI.resetPasswordWithOtp({ email, otp, newPassword });
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await authAPI.resendOtp(email);
      setOtpError('');
      setSuccess('OTP resent to your email');
    } catch (err: any) {
      setOtpError(err.response?.data?.message || 'Failed to resend OTP');
    }
  };

  const handleCancelOtp = () => {
    setShowOtpModal(false);
    setOtpError('');
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-title">
          <Key size={32} style={{ marginBottom: '0.5rem' }} />
          <div>GymsSync - Reset Password</div>
        </div>

        {!showResetForm ? (
          /* Email Form */
          <form onSubmit={handleSendOtp} className="auth-form">
            {error && (
              <div style={{ color: '#dc3545', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1rem' }}>
                {error}
              </div>
            )}
            
            {success && !showOtpModal && (
              <div style={{ color: '#10B981', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1rem', fontWeight: '500' }}>
                {success}
              </div>
            )}
            
            <p style={{ color: '#6B7280', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1.5rem' }}>
              Enter your email address and we'll send you an OTP to reset your password.
            </p>

            <div className="form-group">
              <label className="form-label" style={{ color: '#1a1a1a', fontWeight: 600 }}>
                <Mail size={16} style={{ display: 'inline', marginRight: '0.5rem', color: '#1a1a1a' }} />
                Email
              </label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                style={{ color: '#333', backgroundColor: '#fff' }}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          /* Reset Password Form */
          <form onSubmit={handleResetPassword} className="auth-form">
            {error && (
              <div style={{ color: '#dc3545', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1rem' }}>
                {error}
              </div>
            )}
            
            {success && (
              <div style={{ color: '#10B981', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1rem', fontWeight: '600' }}>
                {success}
              </div>
            )}
            
            <p style={{ color: '#10B981', fontSize: '0.875rem', textAlign: 'center', marginBottom: '1.5rem', fontWeight: '500' }}>
              ✓ OTP verified! Enter your new password.
            </p>

            <div className="form-group">
              <label className="form-label" style={{ color: '#1a1a1a', fontWeight: 600 }}>
                <Lock size={16} style={{ display: 'inline', marginRight: '0.5rem', color: '#1a1a1a' }} />
                New Password
              </label>
              <input
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength={6}
                style={{ color: '#333', backgroundColor: '#fff' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ color: '#1a1a1a', fontWeight: 600 }}>
                <Lock size={16} style={{ display: 'inline', marginRight: '0.5rem', color: '#1a1a1a' }} />
                Confirm Password
              </label>
              <input
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                minLength={6}
                style={{ color: '#333', backgroundColor: '#fff' }}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
        
        <div className="auth-link">
          <Link to="/login">
            <ArrowLeft size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
            Back to Login
          </Link>
        </div>

        {!showResetForm && (
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#EFF6FF', borderRadius: '8px', fontSize: '0.875rem', color: '#1E40AF' }}>
            <strong>💡 Note:</strong> The OTP is valid for 10 minutes. Check your spam folder if you don't receive it.
          </div>
        )}
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <OtpModal
          email={email}
          password="" // Not needed for forgot password
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          onCancel={handleCancelOtp}
          loading={isLoading}
          error={otpError}
        />
      )}
    </div>
  );
};

export default ForgotPassword;

